import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Breadcrumbs,
    Link,
    Alert,
    CircularProgress,
    Divider,
    useTheme,
    alpha,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit as EditIcon } from '@mui/icons-material';
import MDEditor from '@uiw/react-md-editor';
import Cookies from 'js-cookie';
import { ArticleResponse } from '../../types';
import { useThemeMode } from '../../App';
import '../markdown-styles.css';

const ADMIN_COOKIE_KEY = 'zerde_admin_token';
const API_URL = 'https://guide.zerdegroup.kz/api/';

const ArticleView: React.FC = () => {
    const navigate = useNavigate();
    const { articleId } = useParams();
    const [article, setArticle] = useState<ArticleResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { mode } = useThemeMode();
    const theme = useTheme();

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                setLoading(true);
                setError('');
                const token = Cookies.get(ADMIN_COOKIE_KEY);

                if (!token) {
                    navigate('/auth');
                    return;
                }

                const response = await fetch(`${API_URL}/articles/${articleId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Ошибка при загрузке статьи');
                }

                const data = await response.json();
                setArticle(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статьи');
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
    }, [articleId, navigate]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!article) {
        return (
            <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                    Статья не найдена
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
                <Breadcrumbs 
                    aria-label="breadcrumb"
                    sx={{
                        '& .MuiBreadcrumbs-separator': {
                            mx: 1,
                            color: alpha(theme.palette.text.secondary, 0.4),
                        },
                        '& a': {
                            color: alpha(theme.palette.text.secondary, 0.7),
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            transition: 'all 0.2s',
                            '&:hover': {
                                color: 'primary.main',
                            }
                        }
                    }}
                >
                    <Link
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/admin/panel/articles')}
                    >
                        Статьи
                    </Link>
                    <Typography 
                        color="text.primary"
                        sx={{ fontSize: '0.875rem' }}
                    >
                        {article.title}
                    </Typography>
                </Breadcrumbs>
                <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/admin/panel/articles/edit/${article.id}`)}
                    size="small"
                >
                    Редактировать
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            color: alpha(theme.palette.text.secondary, 0.7),
                            fontSize: '0.875rem'
                        }}
                    >
                        Категория: {article.category.title} / {article.subcategory.title}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 2}}>
                        <Chip
                            label={article.is_published ? 'Опубликовано' : 'Черновик'}
                            color={article.is_published ? 'success' : 'warning'}
                            size="small"
                            sx={{ height: '24px' }}
                        />
                        <Chip
                            label={`Автор: ${article.author.login}`}
                            color={article.author.role === 'admin' ? 'primary' : 'default'}
                            size="small"
                            sx={{ height: '24px' }}
                        />
                    </Box>
                </Box>
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 800,
                        color: 'text.primary',
                        mb: 3
                    }}
                >
                    {article.title}
                </Typography>
            </Box>

            <Box 
                data-color-mode={mode}
                className="markdown-content"
                sx={{ 
                    '& pre': {
                        borderRadius: 2,
                        p: 3,
                        backgroundColor: mode === 'dark' ? alpha('#000', 0.3) : alpha('#f6f8fa', 0.8),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                    },
                    '& code': {
                        fontFamily: 'monospace',
                        backgroundColor: mode === 'dark' ? alpha('#000', 0.3) : alpha('#f6f8fa', 0.8),
                        p: 0.5,
                        borderRadius: 1,
                    },
                    '& img': {
                        maxWidth: '100%',
                        borderRadius: 2,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                    },
                    '& blockquote': {
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        pl: 2,
                        ml: 0,
                        backgroundColor: mode === 'dark' ? alpha('#000', 0.2) : alpha('#f6f8fa', 0.5),
                        py: 1,
                        borderRadius: '0 4px 4px 0',
                    },
                    '& a': {
                        color: 'primary.main',
                        textDecoration: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark',
                        }
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 4,
                        mb: 2,
                        fontWeight: 700,
                        position: 'relative',
                        pl: 3,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: '70%',
                            backgroundColor: 'primary.main',
                            borderRadius: 4,
                        }
                    },
                    '& h1': { fontSize: '2.5rem' },
                    '& h2': { fontSize: '2rem' },
                    '& h3': { fontSize: '1.75rem' },
                    '& h4': { fontSize: '1.5rem' },
                    '& ul, & ol': {
                        pl: 3,
                        mb: 3,
                        '& li': {
                            mb: 1,
                        }
                    }
                }}
            >
                <MDEditor.Markdown 
                    source={article.content}
                    style={{ 
                        backgroundColor: 'transparent',
                        color: theme.palette.text.primary
                    }}
                />
            </Box>

            <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex',
                gap: 3,
                color: alpha(theme.palette.text.secondary, 0.7)
            }}>
                <Typography variant="caption">
                    Создано: {new Date(article.created_at!).toLocaleString()}
                </Typography>
                <Typography variant="caption">
                    Обновлено: {new Date(article.updated_at!).toLocaleString()}
                </Typography>
            </Box>
        </Box>
    );
};

export default ArticleView; 